import { useSnackbar } from 'notistack';
import React from 'react';
import { useSelector } from 'react-redux';
import type { Job, ResponseGetJob } from '../../app/ApiGen';
import { useUpdateJob, useUpdateJobCustomer } from '../../app/ApiGen';
import { Card, type OnSubmit, Section } from '../../design-system';
import { selectDerivedState } from '../../store/root';
import {
  CustomerDetails,
  customerDetailsFields,
  customerDetailsValidationSchema,
  toCustomerDetailsFields,
} from '../../utils/jobs/customer';
import type { Normalised } from '../../utils/request';
import { errorSnackbar } from '../ErrorSnackbar';
import { Form } from '../Form';

interface CustomerDetailsSectionProps {
  job: Normalised<Job>;
  refetchJob: () => Promise<ResponseGetJob | null>;
}

export const CustomerDetailsSection: React.VFC<CustomerDetailsSectionProps> = ({
  job,
  refetchJob,
}: CustomerDetailsSectionProps) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { isEvnexAdmin } = useSelector(selectDerivedState);

  const [isEditing, setIsEditing] = React.useState<boolean>();

  const { id: jobId, customer } = job;
  const customerId = customer?.id ?? '';

  const { mutate: updateCustomer } = useUpdateJobCustomer({ jobId });
  const { mutate: updateJob } = useUpdateJob({ jobId });

  const onSubmit = React.useCallback<OnSubmit<CustomerDetails>>(
    async ({ contactPhoneNumber, customerName }, { setSubmitting }) => {
      try {
        await updateCustomer({ data: { id: customerId, type: 'users', attributes: { name: customerName } } });
        await updateJob({ data: { id: job.id, type: 'jobs', attributes: { contactPhoneNumber } } });
        enqueueSnackbar('Customer updated', { variant: 'success' });
        setIsEditing(false);
        await refetchJob();
      } catch (err) {
        errorSnackbar('Unable to edit customer', closeSnackbar, enqueueSnackbar, err);
      } finally {
        setSubmitting(false);
      }
    },
    [closeSnackbar, customerId, enqueueSnackbar, job.id, refetchJob, updateCustomer, updateJob],
  );

  return (
    <Section>
      <Card title="Customer">
        <Form<CustomerDetails>
          fields={customerDetailsFields({
            emailDisabled: true,
            emailHelperText:
              'Please contact Evnex support on 0800 395 007 (NZ) or 1800 959 377 (Aus) if you need to edit the customer email address',
          })}
          initialValues={toCustomerDetailsFields(job)}
          isEditing={isEditing}
          setIsEditing={isEvnexAdmin ? setIsEditing : undefined}
          onSubmit={onSubmit}
          validationSchema={customerDetailsValidationSchema}
        />
      </Card>
    </Section>
  );
};
